import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RouterProvider } from "./context/RouterContext";
import { errMsg } from "./lib/api";
import Layout from "./components/Layout";
import PinLock from "./components/PinLock";
import Login from "./pages/Login";
import Portal from "./pages/Portal";
import VoucherChat from "./pages/VoucherChat";
import Dashboard from "./pages/Dashboard";
import Routers from "./pages/Routers";
import Vouchers from "./pages/Vouchers";
import VoucherDelete from "./pages/VoucherDelete";
import PppSecrets from "./pages/PppSecrets";
import SemuaWifiRumahan from "./pages/SemuaWifiRumahan";
import OnlineUsers from "./pages/OnlineUsers";
import UserManagement from "./pages/UserManagement";
import DepositHistory from "./pages/DepositHistory";
import AdminTasks from "./pages/AdminTasks";
import VoucherTerpakai from "./pages/VoucherTerpakai";
import StarlinkFup from "./pages/StarlinkFup";
import MutasiSaldo from "./pages/MutasiSaldo";
import PesanAlat from "./pages/PesanAlat";
import ResetVoucher from "./pages/ResetVoucher";
import BackupRestore from "./pages/BackupRestore";
import BulananStarlink from "./pages/BulananStarlink";
import PiutangSaldo from "./pages/PiutangSaldo";
import HutangSaldo from "./pages/HutangSaldo";
import Aset from "./pages/Aset";
import Stok from "./pages/Stok";
import SalesReport from "./pages/SalesReport";
import Omset from "./pages/Omset";
import Agents from "./pages/Agents";
import BalanceManager from "./pages/BalanceManager";
import AgentTopup from "./pages/AgentTopup";
import Complaints from "./pages/Complaints";
import WifiComplaints from "./pages/WifiComplaints";
import PppComplaints from "./pages/PppComplaints";
import PetaOdcOdp from "./pages/PetaOdcOdp";
import HpCustomers from "./pages/HpCustomers";
import PppDueSoon from "./pages/PppDueSoon";
import ResetMencurigakan from "./pages/ResetMencurigakan";
import SetupAgen from "./pages/SetupAgen";
import BukaCabang from "./pages/BukaCabang";
import AgentMutasi from "./pages/AgentMutasi";
import PaymentHub from "./pages/PaymentHub";
import Pengaturan from "./pages/Pengaturan";
import Winbox from "./pages/Winbox";
import LaporanAlat from "./pages/LaporanAlat";
import NetData from "./pages/NetData";
import RouterBoard from "./pages/RouterBoard";
import TrafficMonitor from "./pages/TrafficMonitor";
import BhpUso from "./pages/BhpUso";
import LacakAset from "./pages/LacakAset";
import VoucherTuanRumah from "./pages/VoucherTuanRumah";
import LaporanMikhmon from "./pages/LaporanMikhmon";
import EvaluasiPpp from "./pages/EvaluasiPpp";
import PembayaranPortal from "./pages/PembayaranPortal";
import LaporanTeknisi from "./pages/LaporanTeknisi";
import TeknisiPortal from "./pages/teknisi/TeknisiPortal";
import PenghasilanAgen from "./pages/PenghasilanAgen";
import LogAktivitas from "./pages/LogAktivitas";
import PendapatanKotor from "./pages/PendapatanKotor";
import PendingActivation from "./pages/PendingActivation";
import BantuanLogin from "./pages/BantuanLogin";

function AuthCallback() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    googleLogin(sessionId)
      .then(() => navigate("/", { replace: true }))
      .catch((e) => {
        toast.error(errMsg(e));
        navigate("/login", { replace: true });
      });
  }, [googleLogin, location.hash, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback-loading">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <div className="text-sm text-muted-foreground font-mono">Memverifikasi akun Google...</div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <div className="text-sm text-muted-foreground font-mono">Memuat FASNET WIFI...</div>
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <PendingActivation />;
  if (user.pin_verified !== true) return <PinLock />;
  return children;
}

function MenuGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const hidden = user?.hidden_menus || [];
  const isAdminUser = ["admin", "admin_fasnet"].includes(user?.role);
  const blocked = !isAdminUser && hidden.some((p) => (p === "/" ? location.pathname === "/" : location.pathname === p || location.pathname.startsWith(p + "/")));
  if (blocked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="menu-blocked">
        <div className="text-center space-y-3 max-w-sm px-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-2xl">🚫</div>
          <div className="font-heading font-bold text-lg">Akses Dibatasi</div>
          <p className="text-sm text-muted-foreground">Menu ini dinonaktifkan untuk akun Anda oleh admin. Hubungi admin jika membutuhkan akses.</p>
        </div>
      </div>
    );
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Deteksi session_id secara sinkron saat render (mencegah race condition dengan auth check)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/voucherchat" element={<VoucherChat />} />
      <Route path="/teknisi/*" element={<TeknisiPortal />} />
      <Route path="/bantuan-login" element={<BantuanLogin />} />
      <Route
        element={
          <Protected>
            <RouterProvider>
              <MenuGuard>
                <Layout />
              </MenuGuard>
            </RouterProvider>
          </Protected>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/saldo" element={<BalanceManager />} />
        <Route path="/isi-saldo" element={<AgentTopup />} />
        <Route path="/routers" element={<Routers />} />
        <Route path="/routerboard" element={<RouterBoard />} />
        <Route path="/trafik" element={<TrafficMonitor />} />
        <Route path="/bhp-uso" element={<BhpUso />} />
        <Route path="/vouchers" element={<Vouchers />} />
        <Route path="/vouchers/hapus" element={<VoucherDelete />} />
        <Route path="/ppp" element={<PppSecrets />} />
        <Route path="/semua-wifi-rumahan" element={<SemuaWifiRumahan />} />
        <Route path="/hotspot-online" element={<OnlineUsers type="hotspot" />} />
        <Route path="/ppp-online" element={<OnlineUsers type="ppp" />} />
        <Route path="/laporan/omset" element={<Omset />} />
        <Route path="/laporan/ppp" element={<SalesReport type="ppp" title="Transaksi PPPoE" />} />
        <Route path="/laporan/wifi" element={<SalesReport type="wifi" title="Laporan Penjualan WiFi FASNET" />} />
        <Route path="/laporan/voucher-wifi" element={<LaporanMikhmon type="voucher" />} />
        <Route path="/laporan/wifi-rumahan" element={<LaporanMikhmon type="ppp" />} />
        <Route path="/laporan/evaluasi-ppp" element={<EvaluasiPpp />} />
        <Route path="/pembayaran-portal" element={<PembayaranPortal />} />
        <Route path="/log-aktivitas" element={<LogAktivitas />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/history-deposit" element={<DepositHistory />} />
        <Route path="/tugas-admin" element={<AdminTasks />} />
        <Route path="/laporan-teknisi" element={<LaporanTeknisi />} />
        <Route path="/pesan-alat" element={<PesanAlat />} />
        <Route path="/peta-odc-odp" element={<PetaOdcOdp />} />
        <Route path="/starlink" element={<NetData kind="starlink" />} />
        <Route path="/telkom" element={<NetData kind="telkom" />} />
        <Route path="/indosat" element={<NetData kind="indosat" />} />
        <Route path="/moratel" element={<NetData kind="moratel" />} />
        <Route path="/indibiz" element={<NetData kind="indibiz" />} />
        <Route path="/reset-voucher" element={<ResetVoucher />} />
        <Route path="/backup-restore" element={<BackupRestore />} />
        <Route path="/bulanan-starlink" element={<BulananStarlink />} />
        <Route path="/starlink-fup" element={<StarlinkFup />} />
        <Route path="/piutang-saldo" element={<PiutangSaldo />} />
        <Route path="/hutang-saldo" element={<HutangSaldo />} />
        <Route path="/aset" element={<Aset />} />
        <Route path="/lacak-aset" element={<LacakAset />} />
        <Route path="/voucher-tuan-rumah" element={<VoucherTuanRumah />} />
        <Route path="/stok" element={<Stok />} />
        <Route path="/agen" element={<Agents />} />
        <Route path="/komplain" element={<Complaints />} />
        <Route path="/komplain-wifi" element={<WifiComplaints />} />
        <Route path="/komplain-ppp" element={<PppComplaints />} />
        <Route path="/pelanggan-hp" element={<HpCustomers />} />
        <Route path="/ppp-jatuh-tempo" element={<PppDueSoon />} />
        <Route path="/laporan/reset-mencurigakan" element={<ResetMencurigakan />} />
        <Route path="/laporan/setup-agen" element={<SetupAgen />} />
        <Route path="/buka-cabang" element={<BukaCabang />} />
        <Route path="/mutasi-saya" element={<AgentMutasi />} />
        <Route path="/pembayaran" element={<PaymentHub />} />
        <Route path="/pengaturan" element={<Pengaturan />} />
        <Route path="/winbox" element={<Winbox />} />
        <Route path="/laporan/voucher-terpakai" element={<VoucherTerpakai />} />
        <Route path="/laporan/alat" element={<LaporanAlat />} />
        <Route path="/laporan/pendapatan-kotor" element={<PendapatanKotor />} />
        <Route path="/mutasi-saldo" element={<MutasiSaldo />} />
        <Route path="/laporan/penghasilan-agen" element={<PenghasilanAgen />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [toastPos, setToastPos] = useState(() => (window.innerWidth >= 1024 ? "top-right" : "top-center"));
  useEffect(() => {
    const onResize = () => setToastPos(window.innerWidth >= 1024 ? "top-right" : "top-center");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster richColors position={toastPos} theme="light" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
